"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { ShoppingBag, ChevronLeft, Package, Clock, CheckCircle2, XCircle, RotateCcw } from "lucide-react"
import { getOrderList } from "@/lib/api/order"
import type { OrderItem } from "@zrt-ai-office/types"

/* ── 状态筛选选项 ── */
const STATUS_TABS = [
  { label: "全部", value: -1 },
  { label: "待支付", value: 0 },
  { label: "支付中", value: 1 },
  { label: "已支付", value: 2 },
  { label: "支付失败", value: 3 },
  { label: "已撤销", value: 4 },
  { label: "已退款", value: 5 },
  { label: "已关闭", value: 6 },
]

/* ── 工具函数 ── */
function formatPrice(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "-"
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function OrderStatusBadge({ status }: { status: number }) {
  const map: Record<number, { label: string; icon: React.ReactNode; className: string }> = {
    0: {
      label: "已生成",
      icon: <Clock className="h-3 w-3" />,
      className: "text-slate-600 bg-slate-50 border-slate-200",
    },
    1: {
      label: "支付中",
      icon: <Clock className="h-3 w-3" />,
      className: "text-amber-600 bg-amber-50 border-amber-200",
    },
    2: {
      label: "已支付",
      icon: <CheckCircle2 className="h-3 w-3" />,
      className: "text-emerald-600 bg-emerald-50 border-emerald-200",
    },
    3: {
      label: "支付失败",
      icon: <XCircle className="h-3 w-3" />,
      className: "text-red-500 bg-red-50 border-red-200",
    },
    4: {
      label: "已撤销",
      icon: <XCircle className="h-3 w-3" />,
      className: "text-slate-500 bg-slate-50 border-slate-200",
    },
    5: {
      label: "已退款",
      icon: <RotateCcw className="h-3 w-3" />,
      className: "text-blue-500 bg-blue-50 border-blue-200",
    },
    6: {
      label: "已关闭",
      icon: <XCircle className="h-3 w-3" />,
      className: "text-slate-400 bg-slate-50 border-slate-200",
    },
  }
  const conf = map[status] ?? { label: "未知", icon: null, className: "text-slate-400 bg-slate-50 border-slate-200" }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${conf.className}`}>
      {conf.icon}
      {conf.label}
    </span>
  )
}

function OrderCard({ order }: { order: OrderItem }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5 flex flex-col sm:flex-row gap-4 hover:shadow-md transition-shadow">
      {/* 商品图 */}
      <div className="shrink-0">
        {order.productImage ? (
          <img
            src={order.productImage}
            alt={order.productTitle}
            className="h-20 w-20 rounded-xl object-cover border border-border/40"
          />
        ) : (
          <div className="h-20 w-20 rounded-xl bg-secondary/40 flex items-center justify-center border border-border/40">
            <Package className="h-8 w-8 text-muted-foreground/50" />
          </div>
        )}
      </div>

      {/* 主信息 */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground line-clamp-2">{order.productTitle}</h3>
          <OrderStatusBadge status={order.status} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{order.skuName} × {order.quantity}</p>
        <p className="mt-1 text-xs text-muted-foreground">订单号：{order.orderNo}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">下单时间：{formatDate(order.createdAt)}</p>

        <div className="mt-3 flex items-center gap-4">
          <div>
            <span className="text-xs text-muted-foreground">实付</span>
            <span className="ml-1 text-base font-bold text-foreground">{formatPrice(order.payAmount)}</span>
          </div>
          {order.status === 2 && order.expiredAt && (
            <div>
              <span className="text-xs text-muted-foreground">到期</span>
              <span className="ml-1 text-xs text-foreground/70">{formatDate(order.expiredAt)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function OrderSkeleton() {
  return (
    <div className="rounded-2xl border border-border/40 bg-card p-4 sm:p-5 flex gap-4 animate-pulse">
      <div className="h-20 w-20 rounded-xl bg-secondary/50 shrink-0" />
      <div className="flex-1 space-y-2.5 pt-1">
        <div className="h-4 w-3/4 bg-secondary/50 rounded" />
        <div className="h-3 w-1/2 bg-secondary/40 rounded" />
        <div className="h-3 w-2/3 bg-secondary/40 rounded" />
        <div className="h-5 w-24 bg-secondary/50 rounded mt-2" />
      </div>
    </div>
  )
}

const PAGE_SIZE = 20

export function MyOrdersPage() {
  const navigate = useNavigate()

  const [activeStatus, setActiveStatus] = useState(-1)
  const [orders, setOrders] = useState<OrderItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchOrders = useCallback(async (p: number, status: number, reset: boolean) => {
    if (reset) {
      setLoading(true)
      setError(null)
    } else {
      setLoadingMore(true)
    }
    try {
      const resp = await getOrderList({ page: p, pageSize: PAGE_SIZE, status })
      if (reset) {
        setOrders(resp.list)
      } else {
        setOrders(prev => [...prev, ...resp.list])
      }
      setTotal(resp.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败，请重试")
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  useEffect(() => {
    setPage(1)
    fetchOrders(1, activeStatus, true)
  }, [activeStatus, fetchOrders])

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1
    setPage(nextPage)
    fetchOrders(nextPage, activeStatus, false)
  }, [page, activeStatus, fetchOrders])

  const hasMore = orders.length < total

  return (
    <div className="min-h-dvh bg-background pt-14">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* 页头 */}
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="返回"
            className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-secondary/60 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-foreground/70" />
            <h1 className="text-lg font-semibold">我的订单</h1>
          </div>
        </div>

        {/* 状态筛选 */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-6 scrollbar-hide">
          {STATUS_TABS.map(tab => (
            <button
              type="button"
              key={tab.value}
              onClick={() => setActiveStatus(tab.value)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-medium border transition-all ${
                activeStatus === tab.value
                  ? "bg-foreground text-background border-foreground"
                  : "border-border/60 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 内容区 */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <OrderSkeleton key={i} />)}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <XCircle className="h-10 w-10 text-destructive/50" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              type="button"
              onClick={() => fetchOrders(1, activeStatus, true)}
              className="mt-1 px-4 py-1.5 rounded-full border border-border/60 text-xs hover:bg-secondary/60 transition-colors"
            >
              重新加载
            </button>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <ShoppingBag className="h-12 w-12 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">暂无订单</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {orders.map(order => (
                <OrderCard key={order.orderNo} order={order} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-6 py-2 rounded-full border border-border/60 text-sm text-muted-foreground hover:bg-secondary/60 transition-colors disabled:opacity-50"
                >
                  {loadingMore ? "加载中…" : "加载更多"}
                </button>
              </div>
            )}

            <p className="mt-4 text-center text-xs text-muted-foreground">
              共 {total} 条订单
            </p>
          </>
        )}
      </div>
    </div>
  )
}
